import { Col, Layout, Row } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { DerivedTeamRoster } from '../../hooks/types';
import HeaderSection from '../HeaderSection';
import Roster from '../Roster';
import RosterDatePicker from '../Roster/RosterDatePicker';
import useQueryTeamRoster from '../../hooks/useQueryTeamRoster';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import type { RosterDisplayMode } from '../Roster/types';
import { onApolloError } from '../../lib/graphql/helpers';
import { useNotify } from '../../hooks/useNotify';
import PageLoadingSpinner from '../PageLoadingSpinner';

/**
 * Renders personal roster for the current logged in user.
 * @returns A stack of rosters per each team that the user has a role in.
 */
export const UserRoster: React.FC<{
  readonly userId: string;
  readonly headerTitle?: string;
}> = ({ userId, headerTitle }) => {
  const { notify } = useNotify();
  const todayDate = new Date();

  const [teamRosters, setTeamRosters] =
    useState<readonly DerivedTeamRoster[]>();

  const [rosterDisplayMode, setRosterDisplayMode] =
    useState<RosterDisplayMode>('weekly');

  const [rosterDates, setRosterDates] = useState<readonly Date[]>([]);

  const { loading: loadingTeamRosterData, refetch: refetchRosters } =
    useQueryTeamRoster(
      rosterDates[0] ?? todayDate,
      rosterDates[rosterDates.length - 1] ?? todayDate,
      onApolloError(notify.error),
      undefined,
      userId,
      (teamRosters) => setTeamRosters(teamRosters)
    );

  useEffect(() => {
    void refetchRosters();
  }, [refetchRosters]);

  const updateRosterDates = useCallback(
    (dates: readonly Date[]) => setRosterDates(dates),
    []
  );
  const displayModeUpdated = useCallback(
    (displayMode: RosterDisplayMode) => setRosterDisplayMode(displayMode),
    []
  );

  return (
    <Layout>
      {headerTitle === undefined ? (
        <></>
      ) : (
        <HeaderSection title={headerTitle} />
      )}
      <Row>
        <Col flex="auto">
          <RosterDatePicker
            datesUpdated={updateRosterDates}
            displayModeUpdated={displayModeUpdated}
          />
          <Roster
            teamRosters={teamRosters ?? []}
            rosterDates={rosterDates}
            rosterDisplayMode={rosterDisplayMode}
            loading={loadingTeamRosterData}
            personalRosterMode
            readOnly
          />
        </Col>
      </Row>
    </Layout>
  );
};

const PersonalRoster = () => {
  const { loggedInUser } = useLoggedInUser();

  if (loggedInUser === undefined) {
    return <PageLoadingSpinner />;
  }

  const title =
    loggedInUser.display_name === undefined
      ? 'My Roster'
      : `${loggedInUser.display_name}'s Roster`;

  return <UserRoster userId={loggedInUser.id} headerTitle={title} />;
};

export default PersonalRoster;
